import { isEqual } from 'lodash'

/**
 * Activity Log 發佈快照差集：母本存完整 diff，子本只存與母本不同的欄位。
 * 寫入在前端 buildPublishActivityDetail；讀取時 expand 還原給畫面。
 */

export const isMasterPublishedDocId = docId => String(docId || '').includes('master')

const rewriteDocIdDeep = (value, fromDocId, toDocId) => {
  if (value == null || !fromDocId || !toDocId || fromDocId === toDocId) return value
  if (typeof value !== 'object') return value
  if (Array.isArray(value)) {
    return value.map(item => rewriteDocIdDeep(item, fromDocId, toDocId))
  }
  const next = {}
  Object.keys(value).forEach((key) => {
    const child = value[key]
    if (key === 'docId' && child === fromDocId) next[key] = toDocId
    else next[key] = rewriteDocIdDeep(child, fromDocId, toDocId)
  })
  return next
}

const walletFromDoc = (docId, entry) => {
  if (entry?.docData?.wallet) return entry.docData.wallet
  const id = String(docId || '')
  if (id.includes('master')) return id.split('#')[0] || ''
  const parts = id.split('#')
  return parts.length >= 2 ? parts[1] : parts[0] || ''
}

const resolveMasterDocId = (published, childDocId, childEntry) => {
  if (childEntry?.masterDocId && published[childEntry.masterDocId]) {
    return childEntry.masterDocId
  }
  const wallet = walletFromDoc(childDocId, childEntry)
  const masterIds = Object.keys(published || {}).filter(isMasterPublishedDocId)
  const match = masterIds.find(id => walletFromDoc(id, published[id]) === wallet)
  if (match) return match
  const fallback = wallet ? `${wallet}#master` : null
  if (fallback && published[fallback]) return fallback
  return null
}

const compactSectionAgainstMaster = (childSection, masterSection, masterDocId, childDocId) => {
  if (!childSection) return childSection
  if (childSection.inheritFromMaster) return childSection
  if (!masterSection) return childSection

  const rewrittenMaster = rewriteDocIdDeep(masterSection, masterDocId, childDocId)
  const delta = { inheritFromMaster: true }
  let hasOverride = false

  Object.keys(childSection).forEach((key) => {
    if (key === 'inheritFromMaster') return
    if (isEqual(childSection[key], rewrittenMaster[key])) return
    delta[key] = childSection[key]
    hasOverride = true
  })

  return hasOverride ? delta : { inheritFromMaster: true }
}

/**
 * 把 publishedDocsDataByDocId 收成差集：母本維持完整，子本 section 與母本相同的欄位不存。
 * 已是差集的資料再跑一次會維持原樣。
 */
export const compactPublishedDocsForActivityLog = (publishedDocsDataByDocId = {}) => {
  if (!publishedDocsDataByDocId || typeof publishedDocsDataByDocId !== 'object') {
    return publishedDocsDataByDocId
  }

  const masterIds = Object.keys(publishedDocsDataByDocId).filter(isMasterPublishedDocId)
  if (!masterIds.length) return publishedDocsDataByDocId

  let changed = false
  const result = { ...publishedDocsDataByDocId }

  Object.entries(publishedDocsDataByDocId).forEach(([docId, entry]) => {
    if (!entry || isMasterPublishedDocId(docId) || entry.inheritFromMaster) return
    const masterDocId = resolveMasterDocId(publishedDocsDataByDocId, docId, entry)
    const masterEntry = masterDocId ? publishedDocsDataByDocId[masterDocId] : null
    if (!masterEntry) return

    const masterSections = masterEntry.diffDataBySectionId || {}
    const childSections = entry.diffDataBySectionId || {}
    const nextSections = {}
    Object.entries(childSections).forEach(([sectionId, childSection]) => {
      nextSections[sectionId] = compactSectionAgainstMaster(
        childSection,
        masterSections[sectionId],
        masterDocId,
        docId,
      )
    })

    changed = true
    result[docId] = {
      ...entry,
      inheritFromMaster: true,
      masterDocId,
      diffDataBySectionId: nextSections,
    }
  })

  return changed ? result : publishedDocsDataByDocId
}

const expandSectionFromMaster = (childSection, masterSection, masterDocId, childDocId) => {
  if (!childSection?.inheritFromMaster) return childSection
  if (!masterSection) {
    const { inheritFromMaster: _flag, ...rest } = childSection
    return rest
  }
  const base = rewriteDocIdDeep(masterSection, masterDocId, childDocId) || {}
  const { inheritFromMaster: _inheritFromMaster, ...overrides } = childSection
  return { ...base, ...overrides }
}

/**
 * 把子本差集還原成完整 publishedDocsDataByDocId。
 * 舊資料沒有 inheritFromMaster 時原樣回傳。
 */
export const expandPublishedDocsFromActivityLog = (publishedDocsDataByDocId = {}) => {
  if (!publishedDocsDataByDocId || typeof publishedDocsDataByDocId !== 'object') {
    return publishedDocsDataByDocId || {}
  }
  const needsExpand = Object.values(publishedDocsDataByDocId).some(entry => entry?.inheritFromMaster)
  if (!needsExpand) return publishedDocsDataByDocId

  const result = { ...publishedDocsDataByDocId }
  Object.entries(publishedDocsDataByDocId).forEach(([docId, entry]) => {
    if (!entry?.inheritFromMaster || isMasterPublishedDocId(docId)) return
    const masterDocId = resolveMasterDocId(publishedDocsDataByDocId, docId, entry)
    const masterSections = publishedDocsDataByDocId[masterDocId]?.diffDataBySectionId || {}
    const childSections = entry.diffDataBySectionId || {}
    const nextSections = {}
    Object.entries(childSections).forEach(([sectionId, childSection]) => {
      nextSections[sectionId] = expandSectionFromMaster(
        childSection,
        masterSections[sectionId],
        masterDocId,
        docId,
      )
    })
    const {
      inheritFromMaster: _inheritFromMaster,
      masterDocId: _masterDocId,
      ...rest
    } = entry
    result[docId] = {
      ...rest,
      diffDataBySectionId: nextSections,
    }
  })
  return result
}
